import { IconButton } from "@mui/material"
import { DraftOutlined, KeyboardArrowDownOutlined, KeyboardArrowUpOutlined } from "@mui-symbols-material/w300"
import type { ColumnDef, ExpandedState } from "@tanstack/react-table"
import { getCoreRowModel } from "@tanstack/react-table"
import { type FC, useMemo } from "react"

import { PRODUCT_TEST_STATUS_TO_COLOR } from "../../constants"
import { getProductTestResultShelfLifeColor, getProductTestStatusLabel } from "../../utils"
import { ProductTestExpandedContent } from "../ProductTestExpandedContent"

import ProductTestRowActions from "./ProductTestRowActions"

import { ColoredLabel } from "@/components/ColoredLabel"
import { AerosBaseTable } from "@/components/Tables/Aeros/components/AerosBaseTable"
import type { ProductTest, ProductTestResult } from "@/graphql/codegen/graphql"
import { formatDate } from "@/utils/date"

interface Props {
  data: ProductTest[]
  isLoading: boolean
  expandedRow: ExpandedState
  tableContainerRef: React.RefObject<HTMLDivElement>
  setExpandedRow: (updater: ExpandedState | ((old: ExpandedState) => ExpandedState)) => void
  onScroll: () => void
  onEdit: (productTest: ProductTest) => void
  onDelete: (productTest: ProductTest) => void
  onViewDocuments: (productTest: ProductTest) => void
  onViewHistory: (productTest: ProductTest) => void
  onOverride: (productTest: ProductTest, productTestResult: ProductTestResult) => void
}

export const ProductTestsTable: FC<Props> = ({
  data,
  isLoading,
  expandedRow,
  tableContainerRef,
  setExpandedRow,
  onScroll,
  onEdit,
  onDelete,
  onViewDocuments,
  onViewHistory,
  onOverride,
}) => {
  const columns = useMemo<ColumnDef<ProductTest>[]>(() => {
    return [
      {
        accessorKey: "lotNumber",
        header: "Lot #",
        size: 65,
      },
      { header: "Ver.", accessorKey: "version", size: 50 },
      { header: "Batch #", accessorKey: "batchNumber", size: 50 },
      {
        header: "MFG Date",
        accessorKey: "manufacturingDate",
        cell: ({ row }) => formatDate(row.original.manufacturingDate),
        size: 65,
      },
      {
        header: "COA Date",
        accessorKey: "coaDate",
        cell: ({ row }) => formatDate(row.original.coaDate),
        size: 65,
      },
      {
        header: "Initial Received",
        accessorKey: "initialReceivedDate",
        cell: ({ row }) => formatDate(row.original.initialReceivedDate),
        size: 65,
      },
      {
        header: "Initial Shipped",
        accessorKey: "initialShippedDate",
        cell: ({ row }) => formatDate(row.original.initialShippedDate),
        size: 65,
      },
      {
        header: "Expires",
        accessorKey: "expirationDate",
        cell: ({ row }) => {
          const expirationDate = formatDate(row.original.expirationDate)
          const backgroundColor = getProductTestResultShelfLifeColor(
            row.original.expirationDate,
            row.original.manufacturingDate
          )

          return <ColoredLabel label={expirationDate} backgroundColor={backgroundColor} />
        },
        size: 65,
      },
      {
        header: "Status",
        accessorKey: "status",
        cell: ({ row }) => {
          const backgroundColor = PRODUCT_TEST_STATUS_TO_COLOR.get(row.original.status)
          const label = getProductTestStatusLabel(row.original.status)

          return <ColoredLabel label={label} backgroundColor={backgroundColor} />
        },
        size: 65,
      },
      {
        header: "Docs",
        accessorKey: "documents",
        cell: ({ row }) => {
          if (!row.original.hasDocuments) {
            return null
          }
          return (
            <IconButton className='justify-self-center' color='primary' onClick={() => onViewDocuments(row.original)}>
              <DraftOutlined />
            </IconButton>
          )
        },
        size: 50,
      },
      {
        accessorKey: "expander",
        enableSorting: false,
        header: "",
        cell: ({ row }) => (
          <IconButton onClick={row.getToggleExpandedHandler()} className='p-1'>
            {row.getIsExpanded() ? <KeyboardArrowUpOutlined /> : <KeyboardArrowDownOutlined />}
          </IconButton>
        ),
        size: 25,
      },
      {
        accessorKey: "menu",
        enableSorting: false,
        header: "",
        cell: ({ row }) => {
          return row.original.isHistorical ? null : (
            <ProductTestRowActions row={row} onEdit={onEdit} onDelete={onDelete} onViewHistory={onViewHistory} />
          )
        },
        size: 35,
      },
    ]
  }, [onEdit, onDelete, onViewDocuments, onViewHistory])

  return (
    <AerosBaseTable<ProductTest>
      slotProps={{
        bodyRow: {
          getClassName: (row) => {
            const isExpanded =
              typeof row.id === "string" && typeof expandedRow === "object" && expandedRow !== null
                ? expandedRow[row.id]
                : false
            if (isExpanded) {
              return row.original.isHistorical ? "bg-tertiary-yellow-100" : "bg-primary-100"
            }
            return row.original.isHistorical ? "bg-tertiary-yellow-100" : ""
          },
        },
        container: {
          ref: tableContainerRef,
          onScroll,
        },
      }}
      features={{
        table: {
          stickyHeader: true,
        },
        tanstackOptions: {
          data,
          columns,
          enableSorting: false,
          getCoreRowModel: getCoreRowModel(),
          state: {
            isLoading,
            expanded: expandedRow,
          },
          onExpandedChange: setExpandedRow,
          getRowCanExpand: () => true,
          renderExpandedContent: (row) => (
            <ProductTestExpandedContent
              productTest={row.original}
              onOverride={(productTestResult) => onOverride(row.original, productTestResult)}
            />
          ),
        },
      }}
      noDataMessage='No test data found'
    />
  )
}
